import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle,
  faArrowLeft,
  faUsers,
  faMessage,
  faSearch,
  faDatabase,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import { getSubscriptionPlans, getCurrentSubscription, getApiBaseUrl } from '../../services/api';
import axios from 'axios';

interface SubscriptionPlan {
  id: string;
  name: string;
  display_name: string;
  tier: string;
  price_monthly_cents: number;
  price_yearly_cents?: number;
  max_clients: number;
  max_messages_per_month: number;
  max_storage_gb: number;
  max_team_members: number;
  seo_pages_per_month: number;
  youtube_minutes_per_month: number;
  features: Record<string, any>;
  is_recommended: boolean;
}

const PlansPage: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get available plans
      const plansData = await getSubscriptionPlans();
      setPlans(plansData);
      
      // Get current subscription
      const subData = await getCurrentSubscription();
      setCurrentPlanId(subData.subscription?.plan?.id || null);
      
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(cents / 100);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  const getFeatureLabel = (key: string): string => {
    const labels: { [key: string]: string } = {
      chat: 'AI Chat Assistant',
      theming: 'Custom Branding',
      analytics: 'Analytics Dashboard',
      citations: 'Source Citations',
      seo_analysis: 'SEO Analysis',
      qa_validation: 'QA Validation',
      knowledge_base: 'Knowledge Base',
      team_management: 'Team Collaboration',
      youtube_integration: 'YouTube Analysis',
      calendar_integration: 'Calendar Booking'
    };
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleSelectPlan = async (planId: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') => {
    try {
      const response = await axios.post(`${getApiBaseUrl()}/billing/checkout-session`, {
        plan_id: planId,
        billing_period: billingPeriod,
        success_url: window.location.origin + '/consultant/billing?success=true',
        cancel_url: window.location.origin + '/consultant/plans'
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <Button 
          variant="outline" 
          onClick={() => navigate('/consultant/billing')}
          className="mb-4"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
          Back to Billing
        </Button>
        <h1 className="text-3xl font-bold">Choose Your Plan</h1>
        <p className="text-gray-600 mt-1">Select the plan that best fits your needs</p>
      </div>

      {/* Billing Period Toggle */}
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              billingPeriod === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`px-6 py-2 rounded-md font-medium transition-colors ${
              billingPeriod === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Yearly
            <span className="ml-2 text-xs text-green-600 font-semibold">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
        {plans.map((plan) => {
          const isCurrentPlan = plan.id === currentPlanId;
          
          return (
            <Card 
              key={plan.id} 
              className={`relative h-full flex flex-col ${isCurrentPlan ? 'ring-2 ring-blue-500' : ''} ${plan.is_recommended ? 'border-blue-500' : ''}`}
            >
              {plan.is_recommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <div className="bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-bold">
                    MOST POPULAR
                  </div>
                </div>
              )}
              {isCurrentPlan && (
                <div className="absolute -top-2 right-4">
                  <Badge className="bg-green-500 text-white px-3 py-1">Current Plan</Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-2xl">{plan.display_name}</CardTitle>
                <div className="mt-4">
                  {billingPeriod === 'monthly' ? (
                    <>
                      <div className="text-4xl font-bold">{formatCurrency(plan.price_monthly_cents)}</div>
                      <div className="text-gray-500">per month</div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-bold">{formatCurrency(plan.price_yearly_cents || plan.price_monthly_cents * 12)}</div>
                      <div className="text-gray-500">per year</div>
                      {plan.price_yearly_cents && (
                        <div className="text-sm text-green-600 mt-1">
                          {formatCurrency((plan.price_yearly_cents / 12))} per month
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="flex-grow flex flex-col">
                <div className="flex-grow">
                  {/* Core Limits */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faUsers} className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-gray-700">Up to <strong>{plan.max_clients}</strong> clients</span>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faMessage} className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-gray-700"><strong>{formatNumber(plan.max_messages_per_month)}</strong> messages/month</span>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faSearch} className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-gray-700"><strong>{plan.seo_pages_per_month}</strong> SEO pages/month</span>
                    </div>
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faDatabase} className="w-4 h-4 text-gray-400 mr-3" />
                      <span className="text-gray-700"><strong>{plan.max_storage_gb} GB</strong> storage</span>
                    </div>
                  </div>
                  
                  {/* Features */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700 mb-2">Includes:</p>
                    {Object.entries(plan.features || {})
                      .filter(([key, value]) => key !== 'widget_integration' && value && typeof value === 'object' && value.enabled)
                      .map(([key]) => (
                        <div key={key} className="flex items-center text-sm">
                          <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3 mr-2 text-green-500" />
                          <span className="text-gray-600">{getFeatureLabel(key)}</span>
                        </div>
                      ))}
                  </div>
                </div>
                
                {/* Action Button */}
                <div className="mt-6">
                  {isCurrentPlan ? (
                    <Button className="w-full" disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-black hover:bg-[#eab308] text-white transition-colors"
                      onClick={() => handleSelectPlan(plan.id, billingPeriod)}
                    >
                      <FontAwesomeIcon icon={faBolt} className="w-4 h-4 mr-2" />
                      Upgrade to {plan.display_name}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PlansPage;